import { useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Loader2,
  RefreshCw,
  Search,
  X,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { fetchEntityMappings } from "@/lib/api";
import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  EntityMappingSyncLog
} from "../../../../api/src/lib/models.js";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const formatDateForInput = (date: Date): string => {
  return date.toISOString().slice(0, 16);
};

const EntityMappingSyncLogs = () => {
  const [parentEntity, setParentEntity] = useState<string>("all");
  const [childEntity, setChildEntity] = useState<string>("all");
  const [status, setStatus] = useState<string>("all");
  const [limit, setLimit] = useState<number>(25);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState<EntityMappingSyncLog | null>(null);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(25);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [timeRange, setTimeRange] = useState<{
    start: Date;
    end: Date;
  } | undefined>(undefined);

  // For table sorting
  const [sorting, setSorting] = useState<SortingState>([
    { id: "timestamp", desc: true },
  ]);

  // For table filtering
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Query to get entity mappings for dropdown options
  const { data: entityMappings, isLoading: mappingsLoading } = useQuery({
    queryKey: ["/api/entity-mappings"],
    queryFn: () => fetchEntityMappings(),
  });

  // Query to get sync logs based on selected filters
  const {
    data: syncLogsData,
    isLoading: syncLogsLoading,
    isRefetching,
    refetch,
  } = useQuery({
    queryKey: [
      "/api/query-custom-events",
      { parentEntity, childEntity, status, page, pageSize, timeRange },
    ],
    queryFn: async () => {
      // Build query parameters for the API
      const params: Record<string, any> = {
        eventName: "EntitySync",
        parentEntity: parentEntity !== "all" ? parentEntity : undefined,
        childEntity: childEntity !== "all" ? childEntity : undefined,
        syncStatus: status !== "all" ? status : undefined,
        limit: pageSize,
        skip: page * pageSize,
        includeStatusCounts: true,
        startTime: timeRange?.start?.toISOString(),
        endTime: timeRange?.end?.toISOString(),
      };

      // Remove undefined properties
      Object.keys(params).forEach(
        (key) => params[key] === undefined && delete params[key],
      );

      const urlParams = new URLSearchParams(params);
      const url = `/api/query-field-mapping-logs${urlParams.toString()}`;

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setTotalCount(data.totalCount || 0); // Update total count
      return data;
    },
  });

  // Get unique parent entities from mappings
  const uniqueParentEntities = entityMappings
    ? Array.from(new Set(entityMappings.map((m) => m.parentEntity)))
    : [];

  // Get unique child entities for selected parent
  const uniqueChildEntities = entityMappings
    ? Array.from(
        new Set(
          entityMappings
            .filter((m) => !parentEntity || m.parentEntity === parentEntity)
            .map((m) => m.childEntity),
        ),
      )
    : [];

  // Get log status counts
  const statusCounts = syncLogsData?.statusCounts || {
    done: 0,
    error: 0,
    no_update_needed: 0,
    pending: 0,
    total: 0,
  };

  // Define table columns
  const columns: ColumnDef<EntityMappingSyncLog>[] = [
    {
      accessorKey: "parentEntity",
      header: "Parent Entity",
      cell: ({ row }) => {
        const parentEntity = row.original.parentEntity;
        return (
          <div className="flex items-center space-x-2">
            <span className="font-medium">{parentEntity}</span>
          </div>
        );
      },
    },
    {
      accessorKey: "childEntity",
      header: "Child Entity",
      cell: ({ row }) => {
        const childEntity = row.original.childEntity || "N/A";
        return (
          <div className="flex items-center space-x-2">
            <span className="font-medium">{childEntity}</span>
          </div>
        );
      },
    },
    {
      accessorKey: "parentId",
      header: "Parent ID",
      cell: ({ row }) => {
        const parentId = row.original.parentId || "N/A";
        return (
          <div className="flex items-center space-x-2">
            <span
              className="font-medium truncate max-w-[150px]"
              title={parentId}
            >
              {parentId}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: "batchId",
      header: "Batch ID",
      cell: ({ row }) => {
        const batchId = row.original.batchId || "N/A";
        return (
          <div className="flex items-center space-x-2">
            <span
              className="font-medium truncate max-w-[150px]"
              title={batchId}
            >
              {batchId}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: "syncStatus",
      header: "Status",
      cell: ({ row }) => {
        const status = row.original.syncStatus;
        let color = "bg-gray-200 text-gray-800";

        if (status === "updated") {
          color = "bg-green-100 text-green-800";
        } else if (status === "error") {
          color = "bg-red-100 text-red-800";
        } else if (status === "pending") {
          color = "bg-blue-100 text-blue-800";
        } else if (status === "no_update_needed" || status === "no_change") {
          color = "bg-yellow-100 text-yellow-800";
        } else if (status === "skipped") {
          color = "bg-purple-100 text-purple-800";
        } else if (status === "no_child_found") {
          color = "bg-orange-100 text-orange-800";
        } else if (status === "no_parent_found") {
          color = "bg-orange-100 text-orange-800";
        }

        return <Badge className={color}>{status}</Badge>;
      },
    },
    {
      accessorKey: "timestamp",
      header: "Timestamp",
      cell: ({ row }) => {
        const timestamp = row.original.timestamp;
        return (
          <div className="text-sm">
            {timestamp instanceof Date
              ? format(timestamp, "yyyy-MM-dd HH:mm:ss")
              : timestamp
              ? format(new Date(timestamp), "yyyy-MM-dd HH:mm:ss")
              : "N/A"}
          </div>
        );
      },
    },

    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const log = row.original;
        const parentId = log.parentId || log.primaryEntityId;
        const recordUrl =
          parentId && log.parentEntity
            ? `https://peterlang.crm4.dynamics.com/main.aspx?etn=${log.parentEntity}&id=${parentId}&pagetype=entityrecord`
            : "#";

        return (
          <div className="flex space-x-2">
            <TooltipProvider>
              <Tooltip delayDuration={300}>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    disabled={!(log.parentId || log.primaryEntityId)}
                    onClick={() => {
                      if (recordUrl !== "#") {
                        window.open(recordUrl, "_blank");
                      }
                    }}
                  >
                    <span className="material-icons text-sm">open_in_new</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>View in Dataverse</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <TooltipProvider>
              <Tooltip delayDuration={300}>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => {
                      setSelectedLog(log as EntityMappingSyncLog);
                      setIsDetailsOpen(true);
                    }}
                  >
                    <span className="material-icons text-sm">info</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>View Log Details</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        );
      },
    },
  ];

  // Create table instance
  const table = useReactTable({
    data: syncLogsData?.logs || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    state: {
      sorting,
      columnFilters,
      pagination: {
        pageIndex: page,
        pageSize: pageSize,
      },
    },
    manualPagination: true,
    pageCount: Math.ceil((totalCount || 0) / pageSize),
    onPaginationChange: (updater) => {
      if (typeof updater === "function") {
        const newPagination = updater({
          pageIndex: page,
          pageSize: pageSize,
        });
        setPage(newPagination.pageIndex);
        setPageSize(newPagination.pageSize);
      }
    },
  });

  const handleSearch = () => {
    if (searchInputRef.current) {
      const searchTerm = searchInputRef.current.value;
      if (searchTerm) {
        table.getColumn("syncLogs")?.setFilterValue(searchTerm);
      } else {
        table.getColumn("syncLogs")?.setFilterValue(undefined);
      }
    }
  };

  const clearSearch = () => {
    if (searchInputRef.current) {
      searchInputRef.current.value = "";
      table.getColumn("syncLogs")?.setFilterValue(undefined);
    }
  };

  const handleRefresh = () => {
    refetch();
  };

  const clearFilters = () => {
    setParentEntity("all");
    setChildEntity("all");
    setStatus("all");
    setTimeRange(undefined);
    setPage(0);
  };

  // Create status filter options
  const statusOptions = [
    { value: "all", label: "All Statuses" },
    { value: "updated", label: "Updated" },
    { value: "error", label: "Error" },
    { value: "pending", label: "Pending" },
    { value: "no_update_needed", label: "No Updates Needed" },
    { value: "no_change", label: "No Change" },
    { value: "no_child_found", label: "No Child Found" },
    { value: "skipped", label: "Skipped" },
    { value: "no_parent_found", label: "No Parent Found" },
  ];

  return (
    <div className="container mx-auto py-8 px-6 space-y-8">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-3xl font-bold tracking-tight pb-2 pl-2">
          Entity Sync Logs
        </h1>
        <Button
          variant="outline"
          onClick={handleRefresh}
          disabled={isRefetching || syncLogsLoading}
        >
          {isRefetching || syncLogsLoading ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2" />
          )}
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="shadow-sm border border-gray-100">
          <CardHeader className="py-3">
            <CardTitle className="text-sm font-medium">Total Records</CardTitle>
          </CardHeader>
          <CardContent className="pt-1">
            <div className="text-2xl font-bold">
              {syncLogsLoading ? (
                <Skeleton className="h-8 w-16" />
              ) : (
                statusCounts.total
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm border border-gray-100">
          <CardHeader className="py-3">
            <CardTitle className="text-sm font-medium">Success</CardTitle>
          </CardHeader>
          <CardContent className="pt-1">
            <div className="text-2xl font-bold text-green-600">
              {syncLogsLoading ? (
                <Skeleton className="h-8 w-16" />
              ) : (
                statusCounts.done
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm border border-gray-100">
          <CardHeader className="py-3">
            <CardTitle className="text-sm font-medium">Errors</CardTitle>
          </CardHeader>
          <CardContent className="pt-1">
            <div className="text-2xl font-bold text-red-600">
              {syncLogsLoading ? (
                <Skeleton className="h-8 w-16" />
              ) : (
                statusCounts.error
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm border border-gray-100">
          <CardHeader className="py-3">
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
          </CardHeader>
          <CardContent className="pt-1">
            <div className="text-2xl font-bold text-blue-600">
              {syncLogsLoading ? (
                <Skeleton className="h-8 w-16" />
              ) : (
                statusCounts.pending
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="shadow-sm">
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Parent Entity</label>
              <Select value={parentEntity} onValueChange={setParentEntity}>
                <SelectTrigger>
                  <SelectValue placeholder="Select Parent Entity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Parent Entities</SelectItem>
                  {mappingsLoading ? (
                    <SelectItem value="loading" disabled>
                      Loading...
                    </SelectItem>
                  ) : (
                    uniqueParentEntities.map((entity) => (
                      <SelectItem key={entity} value={entity}>
                        {entity}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Child Entity</label>
              <Select
                value={childEntity}
                onValueChange={setChildEntity}
                disabled={!parentEntity}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      parentEntity
                        ? "Select Child Entity"
                        : "Select Parent First"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Child Entities</SelectItem>
                  {mappingsLoading ? (
                    <SelectItem value="loading" disabled>
                      Loading...
                    </SelectItem>
                  ) : (
                    uniqueChildEntities.map((entity) => (
                      <SelectItem key={entity} value={entity}>
                        {entity}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Status</label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Select Status" />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-end space-x-2">
              <Button variant="outline" onClick={clearFilters}>
                Clear Filters
              </Button>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Time Range</label>
              <div className="flex space-x-2">
                <div className="flex-1">
                  <label className="text-xs text-gray-500">Start</label>
                  <Input
                    type="datetime-local"
                    className="h-8"
                    value={
                      timeRange?.start
                        ? formatDateForInput(timeRange.start)
                        : ""
                    }
                    onChange={(e) => {
                      const newDate = e.target.value
                        ? new Date(e.target.value)
                        : undefined;
                      setTimeRange((prev) => ({
                        start:
                          newDate || new Date(Date.now() - 24 * 60 * 60 * 1000),
                        end: prev?.end || new Date(),
                      }));
                    }}
                  />
                </div>
                <div className="flex-1">
                  <label className="text-xs text-gray-500">End</label>
                  <Input
                    type="datetime-local"
                    className="h-8"
                    value={
                      timeRange?.end ? formatDateForInput(timeRange.end) : ""
                    }
                    onChange={(e) => {
                      const newDate = e.target.value
                        ? new Date(e.target.value)
                        : undefined;
                      setTimeRange((prev) => ({
                        start:
                          prev?.start ||
                          new Date(Date.now() - 24 * 60 * 60 * 1000),
                        end: newDate || new Date(),
                      }));
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-medium">
            Entity Sync Logs
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-2">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex w-full max-w-sm items-center space-x-2">
                <Input
                  ref={searchInputRef}
                  placeholder="Search logs..."
                  className="h-8"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSearch();
                  }}
                />
                <Button size="sm" variant="secondary" onClick={handleSearch}>
                  <Search className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="ghost" onClick={clearSearch}>
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => table.resetColumnFilters()}
                >
                  Reset Filters
                </Button>
              </div>
            </div>

            {syncLogsLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 5 }).map((_, index) => (
                  <Skeleton key={index} className="h-12 w-full" />
                ))}
              </div>
            ) : syncLogsData?.logs?.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground">No logs found</p>
              </div>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <TableRow key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <TableHead key={header.id}>
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext(),
                                )}
                          </TableHead>
                        ))}
                      </TableRow>
                    ))}
                  </TableHeader>
                  <TableBody>
                    {table.getRowModel().rows?.length ? (
                      table.getRowModel().rows.map((row) => (
                        <TableRow
                          key={row.id}
                          data-state={row.getIsSelected() && "selected"}
                        >
                          {row.getVisibleCells().map((cell) => (
                            <TableCell key={cell.id}>
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext(),
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={columns.length}
                          className="h-24 text-center"
                        >
                          No results.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            )}

            <div className="flex items-center justify-between space-x-2 py-4">
              <div className="flex-1 text-sm text-muted-foreground">
                Showing {page * pageSize + 1} to{" "}
                {Math.min(
                  (page + 1) * pageSize,
                  syncLogsData?.totalCount || 0,
                )}{" "}
                of {syncLogsData?.totalCount || 0} row(s)
              </div>

              <div className="flex items-center space-x-6">
                <div className="flex items-center space-x-2">
                  <p className="text-sm font-medium">Rows per page</p>
                  <Select
                    value={pageSize.toString()}
                    onValueChange={(value) => {
                      const newPageSize = Number(value);
                      setPageSize(newPageSize);
                      setPage(0);
                    }}
                  >
                    <SelectTrigger className="h-8 w-[70px]">
                      <SelectValue placeholder={pageSize.toString()} />
                    </SelectTrigger>
                    <SelectContent side="top">
                      {[10, 25, 50, 100].map((size) => (
                        <SelectItem key={size} value={size.toString()}>
                          {size}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(0)}
                    disabled={page === 0}
                  >
                    <span className="sr-only">Go to first page</span>
                    <ChevronLeft className="h-4 w-4" />
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                  >
                    <span className="sr-only">Go to previous page</span>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <div className="flex items-center gap-1">
                    <span className="text-sm font-medium">
                      Page {page + 1} of{" "}
                      {Math.max(
                        1,
                        Math.ceil((syncLogsData?.totalCount || 0) / pageSize),
                      )}
                    </span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setPage((p) =>
                        Math.min(
                          Math.ceil(
                            (syncLogsData?.totalCount || 0) / pageSize,
                          ) - 1,
                          p + 1,
                        ),
                      )
                    }
                    disabled={
                      page >=
                      Math.ceil((syncLogsData?.totalCount || 0) / pageSize) - 1
                    }
                  >
                    <span className="sr-only">Go to next page</span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setPage(
                        Math.ceil(
                          (syncLogsData?.totalCount || 0) / pageSize,
                        ) - 1,
                      )
                    }
                    disabled={
                      page >=
                      Math.ceil((syncLogsData?.totalCount || 0) / pageSize) - 1
                    }
                  >
                    <span className="sr-only">Go to last page</span>
                    <ChevronRight className="h-4 w-4" />
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EntityMappingSyncLogs;
